import React from "react";
import { motion } from "framer-motion";
import { PlayCircle, ShieldCheck, Trophy, Sparkles, Smartphone, Download, Award } from "lucide-react";

export default function SaasStudentExperience() {
  return (
    <section className="py-24 bg-slate-900 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute right-0 top-0 w-1/2 h-full bg-violet-600/10 skew-x-12 translate-x-32 hidden lg:block blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column: Text description */}
          <div className="lg:col-span-6 space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-300 rounded-full text-sm font-bold border border-violet-500/30">
              <Smartphone size={16} />
              <span>Mobile Learning Platform</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight">
              A Classroom in Their Pockets
            </h2>
            
            <p className="text-slate-400 text-base leading-relaxed font-semibold">
              Give your students a premium mobile application interface. Seamlessly synchronize live lectures, exam tasks, homework boards, leaderboard rankings, and secure digital certificates across all Android and iOS smartphones.
            </p>

            {/* Mobile specs */}
            <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-800">
              <div className="flex items-center gap-3">
                <PlayCircle size={20} className="text-violet-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">Offline video downloads</span>
              </div>
              <div className="flex items-center gap-3">
                <Trophy size={20} className="text-blue-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">Gamified Rank Leaderboards</span>
              </div>
              <div className="flex items-center gap-3">
                <Award size={20} className="text-cyan-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">Verifiable certificates</span>
              </div>
              <div className="flex items-center gap-3">
                <Sparkles size={20} className="text-emerald-400 shrink-0" />
                <span className="text-sm font-bold text-slate-300">AI Tutor Chat 24/7</span>
              </div>
            </div>

            {/* App downloads buttons */}
            <div className="flex flex-wrap gap-4 pt-6">
              <button 
                onClick={() => alert("App Store deployment available for Enterprise White-Label.")}
                className="flex items-center gap-3 px-5 py-2.5 bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-100 transition-colors text-xs"
              >
                <Download size={18} />
                <div>
                  <p className="text-[8px] uppercase opacity-60 leading-none">Download on</p>
                  <p className="leading-none mt-1 font-black">App Store</p>
                </div>
              </button>
              <a 
                href="/app-release.apk" 
                download="Skillzy-Student-LMS.apk"
                className="flex items-center gap-3 px-5 py-2.5 bg-slate-800 text-white border border-slate-700 rounded-xl font-bold hover:bg-slate-750 transition-colors text-xs"
              >
                <Smartphone size={18} />
                <div>
                  <p className="text-[8px] uppercase opacity-60 leading-none">Download APK</p>
                  <p className="leading-none mt-1 font-black">Android App</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Column: Floating Smartphones */}
          <div className="lg:col-span-6 relative h-[520px] flex justify-center items-center">
            
            {/* Phone 1: AI Chat (Left, angled) */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute left-6 z-20 w-56 aspect-[9/19] bg-slate-950 rounded-[2.5rem] border-[6px] border-slate-800 shadow-2xl p-2.5 flex flex-col justify-between overflow-hidden"
            >
              {/* Speaker notch */}
              <div className="w-20 h-4 bg-slate-800 rounded-full mx-auto -mt-2.5 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
              </div>

              {/* Phone Content Screen */}
              <div className="flex-1 bg-slate-900 rounded-[1.8rem] overflow-hidden flex flex-col p-3 text-white text-[8px] justify-between">
                
                {/* Profile header */}
                <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-violet-600/30 text-violet-400 flex items-center justify-center">
                      🤖
                    </div>
                    <span className="font-bold">Skillzy AI Tutor</span>
                  </div>
                  <span className="text-[6px] text-slate-500">Online</span>
                </div>

                {/* Chat feed */}
                <div className="flex-1 space-y-2 mb-2">
                  <div className="bg-slate-800/80 p-2 rounded-xl rounded-tl-none max-w-[140px]">
                    How do I calculate kinetic energy of an expanding gas cylinder?
                  </div>
                  <div className="bg-violet-950/60 border border-violet-900/40 p-2 rounded-xl rounded-tr-none max-w-[140px] ml-auto">
                    Use <span className="font-mono text-violet-300">K = 3/2 n R T</span> where n is moles and T is absolute temperature.
                  </div>
                </div>

                {/* Input box */}
                <div className="bg-slate-800/50 border border-slate-700/30 rounded-lg p-1.5 flex items-center justify-between text-slate-400">
                  <span>Type message...</span>
                  <span>⚡ Send</span>
                </div>
              </div>
            </motion.div>

            {/* Phone 2: Leaderboard (Right, straight, shifted) */}
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute right-6 z-10 w-56 aspect-[9/19] bg-slate-950 rounded-[2.5rem] border-[6px] border-slate-800 shadow-2xl p-2.5 flex flex-col justify-between overflow-hidden"
            >
              {/* Speaker notch */}
              <div className="w-20 h-4 bg-slate-800 rounded-full mx-auto -mt-2.5 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />
              </div>

              {/* Phone Content Screen */}
              <div className="flex-1 bg-slate-900 rounded-[1.8rem] overflow-hidden flex flex-col p-3 text-white text-[8px] justify-between">
                
                {/* Dashboard stats header */}
                <div className="text-center border-b border-slate-800 pb-2 mb-3">
                  <span className="font-bold uppercase tracking-wider text-slate-400">Class Leaderboard</span>
                </div>

                {/* Leaderboard list */}
                <div className="flex-1 space-y-1.5">
                  <div className="flex justify-between items-center bg-slate-800/40 p-2 rounded-lg">
                    <span className="font-bold text-amber-500">1. Neha Sharma</span>
                    <span>1,480 XP</span>
                  </div>
                  <div className="flex justify-between items-center bg-slate-800/40 p-2 rounded-lg">
                    <span className="font-bold text-slate-300">2. Aarav Saxena</span>
                    <span>1,410 XP</span>
                  </div>
                  <div className="flex justify-between items-center bg-slate-850 p-2 rounded-lg border border-violet-500/20">
                    <span className="font-bold text-violet-400">4. Rohan Kumar (You)</span>
                    <span>1,220 XP</span>
                  </div>
                </div>

                {/* Reward banner */}
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-xl p-2 text-center text-white mt-2">
                  <p className="font-bold">Weekly Prize unlocked!</p>
                  <p className="text-[6px] text-violet-100">Physics Champion Badge</p>
                </div>
              </div>
            </motion.div>

          </div>

        </div>
      </div>
    </section>
  );
}
